import "@/App.css";
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { HomePage } from "@/pages/HomePage";
import { FeaturesPage } from "@/pages/FeaturesPage";
import { PricingPage } from "@/pages/PricingPage";
import { FAQPage } from "@/pages/FAQPage";
import { SignupPage } from "@/pages/Auth/SignupPage";
import { DemoPage } from "@/pages/DemoPage";
import { OnboardingPage } from "@/pages/OnboardingPage";

// Legal pages
import { TermsOfService } from "@/pages/legal/TermsOfService";
import { PrivacyPolicy } from "@/pages/legal/PrivacyPolicy";
import { RefundPolicy } from "@/pages/legal/RefundPolicy";

// Dashboard layout + pages
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DeprecatedDashboardWrapper } from "@/components/dashboard/DeprecatedDashboardWrapper";
import { OverviewPage } from "@/pages/Dashboard/OverviewPage";
import { CallsPage } from "@/pages/Dashboard/CallsPage";
import { LeadsPage } from "@/pages/Dashboard/LeadsPage";
import { BookingsPage } from "@/pages/Dashboard/BookingsPage";
import { KnowledgeBasePage } from "@/pages/Dashboard/KnowledgeBasePage";
import { SettingsPage } from "@/pages/Dashboard/SettingsPage";
import { BillingPage } from "@/pages/Dashboard/BillingPage";
import { UsagePage } from "@/pages/Dashboard/UsagePage";
import { EnterpriseDashboard } from "@/pages/Dashboard/EnterpriseDashboard";

// Admin pages
import { AdminLayout } from "@/pages/Admin/AdminLayout";
import { AdminDashboard } from "@/pages/Admin/AdminDashboard";
import { AdminClientsPage } from "@/pages/Admin/AdminClientsPage";
import { AdminAgentsPage } from "@/pages/Admin/AdminAgentsPage";
import { AgentLinkingPage } from "@/pages/Admin/AgentLinkingPage";
import { WebhookHealthPage } from "@/pages/Admin/WebhookHealthPage";
import { QuarantinePage } from "@/pages/Admin/QuarantinePage";
import { AuditLogsPage } from "@/pages/Admin/AuditLogsPage";
import { EmployeeManagementPage } from "@/pages/Admin/EmployeeManagementPage";
import { AdminOnboardingPage } from "@/pages/Admin/AdminOnboardingPage";
import { AdminPendingChangesPage } from "@/pages/Admin/AdminPendingChangesPage";

// Operations/Employee pages
import { 
  OpsLayout, 
  OpsLogin, 
  OpsDashboard, 
  OpsClientsPage, 
  OpsLinkAgentPage, 
  OpsCallsPage,
  OpsSopPage
} from "@/pages/Ops";
import { OpsOnboardingPage } from "@/pages/Ops/OpsOnboardingPage";
import { OpsPendingChangesPage } from "@/pages/Ops/OpsPendingChangesPage";

// Customer login pages
import { CustomerLoginNew } from "@/pages/CustomerLoginNew";
import { CustomerRecover } from "@/pages/CustomerRecover";
import { ForgotPassword } from "@/pages/ForgotPassword";

function App() {
  return (
    <Routes>
      {/* Admin routes (separate auth, dark theme) */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClientsPage />} />
        <Route path="agents" element={<AdminAgentsPage />} />
        <Route path="link-agent" element={<AgentLinkingPage />} />
        <Route path="employees" element={<EmployeeManagementPage />} />
        <Route path="webhook-health" element={<WebhookHealthPage />} />
        <Route path="quarantine" element={<QuarantinePage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="onboarding" element={<AdminOnboardingPage />} />
        <Route path="pending-changes" element={<AdminPendingChangesPage />} />
      </Route>

      {/* Operations/Employee routes (separate auth, green theme) */}
      <Route path="/ops/login" element={<OpsLogin />} />
      <Route path="/ops" element={<OpsLayout />}>
        <Route index element={<OpsDashboard />} />
        <Route path="clients" element={<OpsClientsPage />} />
        <Route path="link-agent" element={<OpsLinkAgentPage />} />
        <Route path="calls" element={<OpsCallsPage />} />
        <Route path="sop" element={<OpsSopPage />} />
        <Route path="onboarding" element={<OpsOnboardingPage />} />
        <Route path="pending-changes" element={<OpsPendingChangesPage />} />
      </Route>

      {/* Customer login routes (API key + password authentication) */}
      <Route path="/customer-login" element={<CustomerLoginNew />} />
      <Route path="/recover-key" element={<CustomerRecover />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* New Enterprise Dashboard (unified view) */}
      <Route path="/dashboard" element={<EnterpriseDashboard />} />
      
      {/* Legacy Dashboard routes (DEPRECATED - Remove after Jan 3, 2025) */}
      <Route path="/dashboard-old" element={<DeprecatedDashboardWrapper><DashboardLayout /></DeprecatedDashboardWrapper>}>
        <Route index element={<OverviewPage />} />
        <Route path="calls" element={<CallsPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Legal pages (NO marketing layout - clean pages) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />

      {/* Marketing site routes (WITH marketing layout) */}
      <Route path="*" element={<Layout><Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/login" element={<Navigate to="/customer-login" replace />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes></Layout>} />
    </Routes>
  );
}

export default App;
